import React, { forwardRef, useImperativeHandle } from 'react'
import { Box, Flex, Stack, useToast } from '@chakra-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { CampaignTitle } from './CampaignTitle'
import { DefaultFormValues } from './DefaultFormValues'
import { RootGroup } from 'components/AdminConsole/Compaign/fields/RootGroup'
import { ItemIdsList } from './ItemIdsList'
import {
  COMPETITION_TYPE_MENU_ITEMS,
  DEFAULT_CAMPAIGN_TYPE,
  DEFAULT_PARAMETER,
  ITEM_TYPE_MENU_ITEMS,
  MAX_RANGE_DAYS,
  RANK_BY_MENU_ITEMS,
  RIDS_ALLOW_PROMOTION,
} from '../utils'
import { CampaignFormProps, CampaignFormValues, createCampaign } from 'redux/actions/campaigns'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { SidsPicker } from './SidsPicker'
import useLocaleText from 'components/useLocaleText'
import dayjs from 'dayjs'
import keys from 'constants/keys'
import { RootState } from 'constants/interfaces'
import { CampaignsDatePicker } from './CampaignsDatePicker'
import { CampaignDropDown } from './CampaignDropDown'

const defaultValues: CampaignFormValues = {
  root_group_id: null,
  campaign_type: DEFAULT_CAMPAIGN_TYPE,
  products_cluster_data: {
    title: '',
    item: [],
    store_id: [],
    from_ts: '',
    to_ts: '',
    app_from_ts: '',
    app_to_ts: '',
    baseline_ts: '',
    item_type: '',
    parameter: DEFAULT_PARAMETER,
    external_clusters_params: {
      rank_by: 0,
      competition_type: 0,
    },
  },
}

const sanitizeFormData = (data: CampaignFormValues): CampaignFormValues => {
  return {
    ...data,
    products_cluster_data: {
      ...data.products_cluster_data,
      from_ts: dayjs(data.products_cluster_data.from_ts).format(keys.DATE_FORMAT_API),
      to_ts: dayjs(data.products_cluster_data.to_ts).format(keys.DATE_FORMAT_API),
      app_from_ts: dayjs(data.products_cluster_data.app_from_ts).format(keys.DATE_FORMAT_API),
      app_to_ts: dayjs(data.products_cluster_data.app_to_ts).endOf('day').format(keys.DATE_FORMAT_API),
      baseline_ts: dayjs(data.products_cluster_data.baseline_ts).format(keys.DATE_FORMAT_API),
      //@ts-ignore
      store_id: data.products_cluster_data.store_id.flatMap((item) => item.value),
      //@ts-ignore
      item: data.products_cluster_data.item.map((item) => item.value),
      external_clusters_params: {
        rank_by: Number(data.products_cluster_data.external_clusters_params.rank_by),
        competition_type: Number(data.products_cluster_data.external_clusters_params.competition_type),
      },
    },
  }
}

interface CampaignFormRef {
  submit: () => void
}

export const CampaignForm = forwardRef<CampaignFormRef, CampaignFormProps>(({ onClose, onCreated, campaign }, ref) => {
  const { rid } = useSelector((state: RootState) => state.config.config)
  const toast = useToast()
  const dispatch: AppDispatch = useDispatch()
  const t_start_date = useLocaleText('t_start_date', true)
  const t_start_of_sampling = useLocaleText('t_start_of_sampling', true)
  const t_start_of_campaign = useLocaleText('t_start_of_campaign', true)
  const t_end_of_campaign = useLocaleText('t_end_of_campaign', true)
  const t_removal_date = useLocaleText('t_removal_date', true)
  const t_rank_by = useLocaleText('t_rank_by', true)
  const t_competition_type = useLocaleText('competition_type', true)
  const t_item_type = useLocaleText('t_item_type', true)
  const t_creation_success = useLocaleText('t_creation_success', true)

  const methods = useForm<CampaignFormValues>({
    defaultValues: campaign || defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const onSubmit = async (data: CampaignFormValues) => {
    try {
      const sanitizedData = sanitizeFormData(data)
      await dispatch(createCampaign(sanitizedData))
      onCreated(sanitizedData)
      onClose()
      toast({
        title: t_creation_success,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Failed to create campaign',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      console.error('Failed to create campaign:', error)
    }
  }

  useImperativeHandle(ref, () => ({
    submit: () => {
      methods.handleSubmit(onSubmit)()
    },
    onClose,
  }))

  const isPromotionAllowed = RIDS_ALLOW_PROMOTION.includes(rid)

  const itemTypeMenuItems = isPromotionAllowed
    ? ITEM_TYPE_MENU_ITEMS
    : ITEM_TYPE_MENU_ITEMS.filter((item) => item.id !== 'promotion')

  return (
    <FormContext {...methods}>
      <Stack direction="column" style={{ rowGap: '20px' }}>
        <CampaignTitle />
        <Box>
          <Flex flexDir={{ base: 'column', md: 'row' }} style={{ columnGap: '10px' }}>
            <CampaignsDatePicker
              name="app_from_ts"
              label={t_start_of_sampling}
              before="from_ts"
              label_after={t_start_date}
              label_before={t_start_of_campaign}
              showTimeInput={false}
            />
            <CampaignsDatePicker
              name="from_ts"
              label={t_start_date}
              after="app_from_ts"
              before="baseline_ts"
              label_before={t_removal_date}
            />
            <CampaignsDatePicker
              name="baseline_ts"
              label={t_start_of_campaign}
              label_after={t_start_of_sampling}
              label_before={t_end_of_campaign}
              after="from_ts"
              before="to_ts"
              showTimeInput={false}
            />
          </Flex>

          <Flex flexDir={{ base: 'column', md: 'row' }} style={{ columnGap: '10px' }}>
            <CampaignsDatePicker
              name="to_ts"
              label={t_end_of_campaign}
              after="baseline_ts"
              before="app_to_ts"
              label_after={t_start_of_campaign}
              label_before={t_removal_date}
              showTimeInput={false}
              rangeValidation={{
                startField: 'app_from_ts',
                endField: 'to_ts',
                maxDays: MAX_RANGE_DAYS,
              }}
            />
            <CampaignsDatePicker
              name="app_to_ts"
              label={t_removal_date}
              after="to_ts"
              label_after={t_end_of_campaign}
              defaultTime="23:59"
            />
          </Flex>
          <Flex flexDir={{ base: 'column', md: 'row' }} style={{ columnGap: '10px' }}>
            <CampaignDropDown
              name="external_clusters_params.rank_by"
              label={t_rank_by}
              menuItems={RANK_BY_MENU_ITEMS}
            />
            <CampaignDropDown
              name="external_clusters_params.competition_type"
              label={t_competition_type}
              menuItems={COMPETITION_TYPE_MENU_ITEMS}
            />
          </Flex>
        </Box>
        <CampaignDropDown name="item_type" label={t_item_type} menuItems={itemTypeMenuItems} />
        <ItemIdsList />
        <SidsPicker name="products_cluster_data.store_id" />
        <RootGroup />
        <DefaultFormValues />
      </Stack>
    </FormContext>
  )
})
